import { useManagedProjects } from "../features/projects/useProjectsAdmin";
import { useMyRostering } from "../features/roster/useRostering";
import { usePurchasePaths } from "../platform/PlatformContext";
import { hasAccessRight } from "../shared/auth/accessRights";
import { useInstallation } from "../pwa/InstallContext";
import { visibleNavigation } from "./navigation";
import type { NavigationItem } from "./navigation";

/**
 * Returns the navigation entries visible to the person on this device: by role, without the
 * installation hint once installed and without billing in the native apps (ADR-0087), with the
 * rostering entries while rostering is active and the person plans (F-62, `GET /me/rostering`),
 * with „Projekte“ for project responsibles without the right to manage projects (T-125, F-74;
 * `GET /projects` lists only their projects).
 *
 * @param roles - Roles from `GET /me`.
 * @returns The visible entries in display order.
 */
export function useVisibleNavigation(roles: readonly string[]): readonly NavigationItem[] {
  const { state } = useInstallation();
  const purchasePaths = usePurchasePaths();
  const rostering = useMyRostering(hasAccessRight(roles, "AccessOwnRecords")).data;
  const asResponsible =
    hasAccessRight(roles, "AccessOwnRecords") && !hasAccessRight(roles, "ManageProjects");
  const responsibleProjects = useManagedProjects(asResponsible).data;
  const visible = visibleNavigation(roles, {
    installed: state === "installed",
    purchasePaths,
    rostering:
      rostering === undefined ? undefined : { active: rostering.active, plans: rostering.plans },
    projectResponsible: asResponsible && (responsibleProjects?.length ?? 0) > 0,
  });

  return visible;
}
